import { useEffect, useMemo, useState } from "react";
import { settingsApi, type Setting } from "@/lib/settings.api";
import { SETTING_COPY, type SystemSettingsCategoryId } from "../systemSettings.constants";

export function useSystemSettings(categoryId: SystemSettingsCategoryId, modules: string[]) {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    settingsApi.list().then((response) => {
      if (!mounted) return;
      const scoped = response.data.filter((setting) => modules.includes(setting.module));
      setSettings(scoped);
      setDrafts(Object.fromEntries(scoped.map((setting) => [setting.key, setting.value])));
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [categoryId, modules]);

  async function saveSetting(key: string) {
    setSaving(key);
    try {
      await settingsApi.update(key, drafts[key]);
      setSettings((current) =>
        current.map((setting) =>
          setting.key === key ? { ...setting, value: drafts[key] } : setting,
        ),
      );
    } finally {
      setSaving(null);
    }
  }

  function setDraft(key: string, value: string) {
    setDrafts((current) => ({ ...current, [key]: value }));
  }

  const grouped = useMemo(
    () =>
      settings.reduce<Record<string, Setting[]>>((acc, setting) => {
        (acc[setting.module] ??= []).push(setting);
        return acc;
      }, {}),
    [settings],
  );

  const dirtyCount = settings.filter((setting) => drafts[setting.key] !== setting.value).length;
  const criticalCount = settings.filter(
    (setting) => (SETTING_COPY[setting.key]?.impact ?? "normal") === "critical",
  ).length;

  return {
    settings,
    drafts,
    grouped,
    loading,
    saving,
    dirtyCount,
    criticalCount,
    moduleCount: Object.keys(grouped).length,
    setDraft,
    saveSetting,
  };
}
